import React, { useState, useEffect } from 'react';

interface Waiter {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  lastUsed?: string;
}

const WaiterManager: React.FC = () => {
  const [waiters, setWaiters] = useState<Waiter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWaiter, setEditingWaiter] = useState<Waiter | null>(null);
  const [waiterName, setWaiterName] = useState('');

  useEffect(() => {
    loadWaiters();
  }, []);

  const loadWaiters = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/v1/waiters');
      const data = await response.json();
      if (data.success) {
        setWaiters(data.waiters);
      }
    } catch (error) {
      console.error('Failed to load waiters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateUniqueWaiterName = (baseName: string, existingWaiters: Waiter[]): string => {
    const existingNames = existingWaiters.map(w => w.name.toLowerCase());
    const baseNameLower = baseName.toLowerCase().trim();

    if (!existingNames.includes(baseNameLower)) {
      return baseName.trim();
    }

    // Add suffix like David-2, David-3, etc.
    let counter = 2;
    let uniqueName = `${baseName.trim()}-${counter}`;

    while (existingNames.includes(uniqueName.toLowerCase())) {
      counter++;
      uniqueName = `${baseName.trim()}-${counter}`;
    }

    return uniqueName;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!waiterName.trim()) {
      return;
    }

    try {
      let finalName = waiterName.trim();

      // Auto-suffix for duplicates (only for new waiters)
      if (!editingWaiter) {
        finalName = generateUniqueWaiterName(waiterName, waiters);
      }

      const url = editingWaiter
        ? `/api/v1/waiters/${editingWaiter.id}`
        : '/api/v1/waiters';

      const response = await fetch(url, {
        method: editingWaiter ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: finalName })
      });

      const data = await response.json();
      if (data.success) {
        // Reset form
        setWaiterName('');
        setShowCreateForm(false);
        setEditingWaiter(null);

        // Show name change notification if auto-suffixed
        if (finalName !== waiterName.trim()) {
          alert(`השם שונה ל-"${finalName}" כיון ששם דומה כבר קיים`);
        }

        await loadWaiters();
      } else {
        alert(data.error || 'Failed to save waiter');
      }
    } catch (error) {
      console.error('Failed to save waiter:', error);
      alert('Failed to save waiter');
    }
  };

  const handleEdit = (waiter: Waiter) => {
    setEditingWaiter(waiter);
    setWaiterName(waiter.name);
    setShowCreateForm(true);
  };

  const handleDelete = async (waiter: Waiter) => {
    if (window.confirm(`האם למחוק את המלצר "${waiter.name}"?`)) {
      try {
        const response = await fetch(`/api/v1/waiters/${waiter.id}`, {
          method: 'DELETE'
        });
        const data = await response.json();
        if (data.success) {
          await loadWaiters();
        } else {
          alert(data.error || 'Failed to delete waiter');
        }
      } catch (error) {
        console.error('Failed to delete waiter:', error);
        alert('Failed to delete waiter');
      }
    }
  };

  const toggleActive = async (waiter: Waiter) => {
    try {
      const response = await fetch(`/api/v1/waiters/${waiter.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !waiter.isActive })
      });
      const data = await response.json();
      if (data.success) {
        await loadWaiters();
      } else {
        alert(data.error || 'Failed to update waiter');
      }
    } catch (error) {
      console.error('Failed to toggle waiter status:', error);
      alert('Failed to update waiter');
    }
  };

  // Render function (not component) to prevent focus loss on parent re-render
  const renderWaiterForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {editingWaiter ? 'ערוך מלצר' : 'הוסף מלצר חדש'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              שם המלצר *
            </label>
            <input
              type="text"
              value={waiterName}
              onChange={(e) => setWaiterName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="שם המלצר"
              autoFocus
            />
            {!editingWaiter && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                אם השם כבר קיים, יתווסף סיומת אוטומטית (למשל: דוד-2)
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {editingWaiter ? 'עדכן מלצר' : 'הוסף מלצר'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setEditingWaiter(null);
                setWaiterName('');
              }}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">ניהול מלצרים</h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            נהל פרופילים של מלצרים לאירועים עתידיים
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          הוסף מלצר חדש
        </button>
      </div>

      {/* Waiters List */}
      {waiters.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            אין מלצרים במאגר
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            התחל בהוספת פרופילים של מלצרים לשימוש באירועים
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            הוסף מלצר ראשון
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    שם המלצר
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    שימוש אחרון
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {waiters.map((waiter) => (
                  <tr key={waiter.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 dark:text-blue-400 font-medium text-sm">
                            {waiter.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {waiter.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          waiter.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}
                      >
                        {waiter.isActive ? 'פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {waiter.lastUsed ? new Date(waiter.lastUsed).toLocaleDateString('he-IL') : 'מעולם'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(waiter)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                        >
                          ערוך
                        </button>
                        <button
                          onClick={() => toggleActive(waiter)}
                          className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300"
                        >
                          {waiter.isActive ? 'השבת' : 'הפעל'}
                        </button>
                        <button
                          onClick={() => handleDelete(waiter)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          מחק
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showCreateForm && renderWaiterForm()}
    </div>
  );
};

export default WaiterManager;